import type { Event } from "@sentry/react";

import { redactLocation } from "@/lib/analytics/redactUrl";

/** Keys under a breadcrumb's / replay frame's `data` whose value is a location. */
const LOCATION_KEYS = ["from", "to", "url", "previous"];

/**
 * The same, for a span's attributes: `instrumentFetchRequest` writes the request URL to `url`
 * and `http.url`, and the OTel semantic conventions name it `url.full`.
 */
const SPAN_LOCATION_KEYS = [...LOCATION_KEYS, "http.url", "url.full"];

/** Span attributes holding the bare `?…` / `#…` tail of that URL, split out beside it. */
const SPAN_URL_TAIL_KEYS = ["http.query", "http.fragment"];

function redactLocationKeys(
  data: Record<string, unknown> | undefined,
  keys: string[] = LOCATION_KEYS
): void {
  if (!data) return;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string") data[key] = redactLocation(value);
  }
}

/**
 * Strip URL credentials from every Sentry event before it leaves the browser.
 *
 * Sentry's `HttpContext` integration sets `event.request.url` from `location.href` — fragment
 * included — on EVERY event, so an error, a pageload transaction or a replay captured before
 * supabase-js's async `detectSessionInUrl` clears the hash carries the whole OAuth fragment
 * (`#access_token=…&refresh_token=…`) to Sentry. The refresh token is long-lived, so this is
 * account-takeover material rather than a session leak. The same applies to `?token_hash=` on
 * /auth/confirm, `?token=` on /invite/accept and the /share/:token path.
 *
 * Installed with `client.addEventProcessor` rather than `beforeSend`: replay events never reach
 * `beforeSend`, but they do run the client's event processors (`prepareReplayEvent` calls
 * `prepareEvent`), and this is the only hook that covers errors, transactions and replays alike.
 * The replay RECORDING payload is a separate envelope item — see `redactReplayRecordingEvent`.
 */
export function redactSentryEvent(event: Event): Event {
  redactRequest(event.request);

  // The transaction name is the raw pathname until a route matches, so /share/<token> lands here.
  if (event.transaction) event.transaction = redactLocation(event.transaction);

  for (const breadcrumb of event.breadcrumbs ?? []) {
    redactLocationKeys(breadcrumb.data);
  }

  // A transaction repeats every URL its fetches touched, one level down from the breadcrumbs.
  for (const span of event.spans ?? []) redactSpan(span);
  redactSpan(event.contexts?.trace);

  redactReplayUrls(event);

  return event;
}

function redactRequest(request: Event["request"]): void {
  if (!request) return;
  if (typeof request.url === "string") request.url = redactLocation(request.url);
  // HttpContext fills Referer from document.referrer, which is the page we came FROM —
  // an /auth/callback or /share/:token referrer leaks exactly the same credential.
  const headers = request.headers;
  if (headers && typeof headers.Referer === "string") {
    headers.Referer = redactLocation(headers.Referer);
  }
}

/**
 * Strip URL credentials from one span of a transaction event — `event.spans[]`, plus the root
 * span the trace context carries.
 *
 * Redacting `request.url` covers the page; a transaction also ships every request made while it
 * was open. `instrumentFetchRequest` names each fetch span `"<METHOD> <url>"` and repeats the URL
 * in its attributes, so a document preview — `storage.createSignedUrl(...)` returns `?token=<jwt>`,
 * a bearer for that object, and the app then fetches it — leaks the token here even though its
 * fetch BREADCRUMB is redacted above. A pageload span described by the landing href is the same
 * exposure on /auth/callback and /share/:token.
 */
function redactSpan(span: { description?: string; data?: Record<string, unknown> } | undefined) {
  if (!span) return;

  if (typeof span.description === "string") {
    // Per whitespace-separated part, because a fetch span is described as "GET <url>" and a
    // pageload one by the href alone; redactLocation returns a non-location ("GET") untouched.
    span.description = span.description
      .split(" ")
      .map((part) => redactLocation(part))
      .join(" ");
  }

  const data = span.data;
  redactLocationKeys(data, SPAN_LOCATION_KEYS);
  if (!data) return;

  // `http.query` is a bare "?a=b" and `http.fragment` a bare "#a=b" — neither parses as a
  // location on its own, so lend them a path and take it back off. A fragment is dropped
  // wholesale (redactUrl does not name its keys), leaving "".
  for (const key of SPAN_URL_TAIL_KEYS) {
    const value = data[key];
    if (typeof value === "string") data[key] = redactLocation("/" + value).slice(1);
  }
}

/** A replay event lists every URL of its segment in a field of its own. */
function redactReplayUrls(event: Event): void {
  const urls = (event as Event & { urls?: unknown }).urls;
  if (!Array.isArray(urls)) return;
  (event as Event & { urls: unknown[] }).urls = urls.map((url) =>
    typeof url === "string" ? redactLocation(url) : url
  );
}

/** The bit of rrweb's custom-event shape this needs; the SDK's own type is not exported. */
interface RecordingFrameEvent {
  data?: { tag?: string; payload?: unknown };
}

/**
 * Strip URL credentials from a Session Replay recording frame.
 *
 * The recording travels as its own envelope attachment, so no event processor sees it. Its
 * performance frames carry the URL as `payload.description` — `navigation.navigate` is the
 * landing href, `navigation.push` each client-side navigation — and its breadcrumb frames
 * carry `from`/`to`. With `replaysOnErrorSampleRate: 1.0`, any error on the callback page
 * would otherwise upload the fragment.
 *
 * This covers only rrweb's CUSTOM frames (`type: 5`) — the SDK applies
 * `beforeAddRecordingEvent` to those alone. rrweb's own Meta frame (`type: 4`) carries the
 * href too and can never reach this; `lib/sentry/replayGate` is what keeps a credential out
 * of THAT one, by not starting the recorder until the URL is clean.
 */
export function redactReplayRecordingEvent<T extends RecordingFrameEvent>(event: T): T {
  const payload = event.data?.payload as
    | { description?: unknown; data?: Record<string, unknown> }
    | undefined;
  if (!payload || typeof payload !== "object") return event;

  if (typeof payload.description === "string") {
    payload.description = redactLocation(payload.description);
  }
  redactLocationKeys(payload.data);

  return event;
}
